'use client';

import { useFormatAmount, useFormatter } from '@capsicum/common';
import { roundDownNum, useConversionRatesQuery } from '@capsicum/socios-core';
import { useEffect, useState } from 'react';

import { TypeCurrency } from '../models';

import { useWalletCurrencies } from './useWalletCurrencies';
import { useWalletWithdrawalMinimumAmount } from './useWalletWithdrawalMinimumAmount';
import { useWithdrawRemainingLimit } from './useWithdrawRemainingLimit';

export const useWithdrawState = (fanTokenId?: string, isExternalToken?: boolean) => {
	const { formatAmount } = useFormatAmount();
	const { formatCrypto, formatFiat } = useFormatter();
	const { data, error: currenciesError, isLoading: isCurrenciesLoading } = useWalletCurrencies();
	const { altCurrency, defaultCurrency } = data || {};
	const currencyId = fanTokenId ? fanTokenId : data?.defaultCurrency?.id;
	const conversionRatesParams = !altCurrency
		? [{ from: currencyId, to: defaultCurrency?.id }]
		: [
				{ from: currencyId, to: altCurrency?.id },
				{ from: altCurrency?.id, to: currencyId }
			];
	const {
		data: conversionRates,
		error: conversionRatesError,
		isLoading: isConversionRatesLoading
	} = useConversionRatesQuery(conversionRatesParams);
	const {
		error: minAmountError,
		isLoading: minCHZLoading,
		minAmount: minCHZ
	} = useWalletWithdrawalMinimumAmount(TypeCurrency.Crypto);
	const { isLoading: minFTLoading, minAmount: minTokens } = useWalletWithdrawalMinimumAmount(TypeCurrency.VoteToken);
	const minFT = isExternalToken ? '0' : minTokens;
	const { cryptoLimit, fiatLimit } = useWithdrawRemainingLimit(data);
	const chzRemaining = isNaN(Number(cryptoLimit?.remainingAmountBeforeLimit))
		? 0
		: Number(cryptoLimit?.remainingAmountBeforeLimit);
	const chzAvailable = roundDownNum(chzRemaining);
	const formattedCHZAvailable = formatCrypto(chzRemaining, 'CHZ');
	const formatedConversionAvailable = formatFiat(
		fiatLimit?.remainingAmountBeforeLimit || 0,
		data?.altCurrency?.shortCode
	);

	const [inputValue, setInputValue] = useState<number | string>(
		fanTokenId ? Number(minFT) || 1 : Number(minCHZ) || 0
	);
	const [inputConvertedPrice, setInputConvertedPrice] = useState<number>(
		Number(inputValue) * Number(conversionRates?.[0]?.rate)
	);
	const conversionAltPrice = `≈${Number(conversionRates?.[0]?.rate).toFixed(2)} ${altCurrency?.shortCode}`;

	const convertedDefaultPrice = `≈${formatAmount(inputConvertedPrice, {
		maximumFractionDigits: 2,
		minimumFractionDigits: 2
	})} ${altCurrency?.shortCode}`;

	const onInputValueChange = (value: number) => {
		setInputConvertedPrice(value);
	};

	const onAmountChange = (amount: string) => {
		if (conversionRates) {
			setInputValue(amount);
			const calculatedRate = Number(amount) * Number(conversionRates?.[0]?.rate);

			onInputValueChange(calculatedRate);
		}
	};

	const onRatesChanged = (rate?: string) => {
		setInputConvertedPrice(Number(inputValue) * Number(rate));
	};

	useEffect(() => {
		if (conversionRates) {
			onRatesChanged(conversionRates[0]?.rate);
		}
	}, [onRatesChanged, conversionRates]);

	return {
		altCurrency,
		chzAvailable,
		conversionAltPrice,
		conversionRates,
		convertedDefaultPrice,
		defaultCurrency,
		formatedConversionAvailable,
		formattedCHZAvailable,
		inputConvertedPrice,
		inputValue,
		isWithdrawError: conversionRatesError || currenciesError || minAmountError,
		isWithdrawLoading: isCurrenciesLoading || isConversionRatesLoading || minCHZLoading || minFTLoading,
		minCHZ,
		minFT,
		onAmountChange,
		onRatesChanged,
		setInputConvertedPrice,
		setInputValue
	};
};
